(() => {
  const root = document.getElementById('sowelu-crystal-guide');
  if (!root || root.dataset.voiceV5Initialized === 'true') return;
  root.dataset.voiceV5Initialized = 'true';

  const micButton = root.querySelector('[data-guide-mic]');
  const input = root.querySelector('[data-guide-input]');
  const inputWrap = root.querySelector('.sowelu-guide__input-wrap');
  const form = root.querySelector('[data-guide-form]');
  const messages = root.querySelector('[data-guide-messages]');
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const chatEndpoint = String(root.dataset.endpoint || '').trim();
  const speechEndpoint = chatEndpoint.replace(/\/chat\/?$/i, '/speech');

  if (!micButton || !input || !inputWrap || !form || !messages) return;

  if ('speechSynthesis' in window) {
    try { window.speechSynthesis.cancel(); } catch (_) {}
  }

  const defaultPlaceholder = input.getAttribute('placeholder') || 'Ask about a stone, intention, or form…';

  const wave = document.createElement('span');
  wave.className = 'sowelu-guide__voice-wave-v3';
  wave.setAttribute('aria-hidden', 'true');
  wave.innerHTML = '<i></i><i></i><i></i><i></i><i></i>';
  inputWrap.insertBefore(wave, micButton);

  const voiceStatus = document.createElement('span');
  voiceStatus.className = 'sowelu-guide__voice-status-v3';
  voiceStatus.setAttribute('aria-live', 'polite');
  inputWrap.insertBefore(voiceStatus, micButton);

  let controllerState = 'idle';
  let generation = 0;
  let nextCycleId = 0;
  let activeCycle = null;
  let restartTimer = null;
  let stopFallbackTimer = null;
  let statusTimer = null;
  let startingText = '';
  let committedCycles = [];
  let outputCommitted = false;

  let activeAudio = null;
  let activePlaybackButton = null;
  let activeObjectUrl = null;
  let playbackAbortController = null;
  const audioCache = new WeakMap();

  function normalize(text) {
    return String(text || '').replace(/\s+/g, ' ').trim();
  }

  function combine(left, right) {
    return normalize([normalize(left), normalize(right)].filter(Boolean).join(' '));
  }

  function comparableWord(word) {
    return String(word || '')
      .toLowerCase()
      .replace(/^[^a-z0-9']+|[^a-z0-9']+$/g, '');
  }

  function orderedText(map) {
    return [...map.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, text]) => normalize(text))
      .filter(Boolean)
      .join(' ');
  }

  // Defensive restart-boundary cleanup only. Durable accounting is based on
  // final result indexes, not on deduplicating giant accumulated strings.
  function stripRestartBoundary(committed, next) {
    const base = normalize(committed);
    const text = normalize(next);
    if (!base) return text;
    if (!text) return '';

    const baseLower = base.toLowerCase();
    const textLower = text.toLowerCase();
    if (textLower === baseLower || baseLower.endsWith(` ${textLower}`)) return '';
    if (textLower.startsWith(`${baseLower} `)) return normalize(text.slice(base.length));

    const baseWords = base.split(/\s+/);
    const nextWords = text.split(/\s+/);
    const maxOverlap = Math.min(baseWords.length, nextWords.length, 10);

    for (let count = maxOverlap; count >= 2; count -= 1) {
      const leftEdge = baseWords.slice(-count).map(comparableWord).join(' ');
      const rightEdge = nextWords.slice(0, count).map(comparableWord).join(' ');
      if (leftEdge && leftEdge === rightEdge) {
        return normalize(nextWords.slice(count).join(' '));
      }
    }

    return text;
  }

  function resizeInput() {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 112)}px`;
  }

  function clearStatusTimer() {
    if (statusTimer) window.clearTimeout(statusTimer);
    statusTimer = null;
  }

  function setVoiceUi(mode, message = '') {
    clearStatusTimer();

    const listening = mode === 'listening';
    const transcribing = mode === 'transcribing';
    const error = mode === 'error';

    inputWrap.classList.toggle('is-voice-listening', listening);
    inputWrap.classList.toggle('is-voice-transcribing', transcribing);
    inputWrap.classList.toggle('is-voice-error', error);
    micButton.classList.toggle('is-listening', listening);
    micButton.classList.toggle('is-transcribing', transcribing);
    micButton.setAttribute('aria-pressed', listening ? 'true' : 'false');
    micButton.setAttribute('aria-disabled', transcribing ? 'true' : 'false');

    if (listening) {
      voiceStatus.textContent = 'Listening';
      input.placeholder = 'Listening… tap ■ to stop & transcribe';
      micButton.setAttribute('aria-label', 'Stop and transcribe');
      micButton.title = 'Stop & transcribe';
      return;
    }

    if (transcribing) {
      voiceStatus.textContent = 'Transcribing…';
      input.placeholder = 'Transcribing…';
      micButton.setAttribute('aria-label', 'Transcribing speech');
      micButton.title = 'Transcribing…';
      return;
    }

    if (error) {
      voiceStatus.textContent = message || 'Try again';
      input.placeholder = message || 'I didn’t catch that — tap the mic and try again';
      micButton.setAttribute('aria-label', 'Speak message');
      micButton.title = 'Speak message';
      statusTimer = window.setTimeout(() => setVoiceUi('idle'), 1900);
      return;
    }

    voiceStatus.textContent = '';
    input.placeholder = defaultPlaceholder;
    micButton.setAttribute('aria-label', SpeechRecognition ? 'Speak message' : 'Voice input unavailable');
    micButton.title = SpeechRecognition ? 'Speak message' : 'Voice input unavailable in this browser';
  }

  function clearRestartTimer() {
    if (restartTimer) window.clearTimeout(restartTimer);
    restartTimer = null;
  }

  function clearStopFallbackTimer() {
    if (stopFallbackTimer) window.clearTimeout(stopFallbackTimer);
    stopFallbackTimer = null;
  }

  function isCurrentCycle(cycle) {
    return Boolean(
      cycle &&
      activeCycle === cycle &&
      cycle.generation === generation &&
      cycle.recognition === activeCycle?.recognition
    );
  }

  function controllerTranscript() {
    return normalize(committedCycles.join(' '));
  }

  function foldCycleOnce(cycle) {
    if (!cycle || cycle.folded) return;
    cycle.folded = true;

    const finalText = orderedText(cycle.finalByIndex);
    if (!finalText) return;

    const cleaned = stripRestartBoundary(controllerTranscript(), finalText);
    if (cleaned) committedCycles.push(cleaned);
  }

  function finishStop(expectedGeneration) {
    if (outputCommitted) return;
    if (expectedGeneration !== generation) return;

    clearRestartTimer();
    clearStopFallbackTimer();

    const cycle = activeCycle;
    const recognizerToAbort = cycle?.recognition || null;
    if (cycle && cycle.generation === expectedGeneration) {
      foldCycleOnce(cycle);
      cycle.ended = true;
    }

    activeCycle = null;
    generation += 1; // invalidate every callback/timer from the completed recording
    controllerState = 'idle';
    outputCommitted = true;

    if (recognizerToAbort) {
      try { recognizerToAbort.abort(); } catch (_) {}
    }

    const transcript = controllerTranscript();
    if (transcript) {
      input.value = combine(startingText, transcript);
      resizeInput();
      input.dispatchEvent(new Event('input', { bubbles: true }));
      setVoiceUi('idle');
      input.focus();
      return;
    }

    setVoiceUi('error', 'I didn’t catch that — try again');
    input.focus();
  }

  function scheduleRecognitionRestart(expectedGeneration) {
    if (expectedGeneration !== generation) return;
    if (!['listening', 'starting'].includes(controllerState)) return;
    if (activeCycle !== null || restartTimer) return;

    controllerState = 'restart-wait';
    setVoiceUi('listening');

    restartTimer = window.setTimeout(() => {
      restartTimer = null;
      if (expectedGeneration !== generation) return;
      if (controllerState !== 'restart-wait') return;
      if (activeCycle !== null) return;

      controllerState = 'starting';
      startRecognitionCycle(expectedGeneration);
    }, 260);
  }

  function startRecognitionCycle(expectedGeneration) {
    if (!SpeechRecognition) return;
    if (expectedGeneration !== generation) return;
    if (!['starting', 'restart-wait', 'listening'].includes(controllerState)) return;
    if (activeCycle !== null) return;

    const recognizer = new SpeechRecognition();
    const cycle = {
      id: ++nextCycleId,
      generation: expectedGeneration,
      recognition: recognizer,
      finalByIndex: new Map(),
      interimByIndex: new Map(),
      ended: false,
      folded: false
    };

    activeCycle = cycle;

    recognizer.lang = 'en-US';
    recognizer.continuous = true;
    recognizer.interimResults = true;
    recognizer.maxAlternatives = 1;

    recognizer.onstart = () => {
      if (!isCurrentCycle(cycle)) return;
      if (controllerState === 'stopping') return;
      controllerState = 'listening';
      setVoiceUi('listening');
    };

    recognizer.onresult = (event) => {
      if (!isCurrentCycle(cycle)) return;
      if (!['starting', 'listening', 'stopping'].includes(controllerState)) return;

      const firstChanged = Number.isInteger(event.resultIndex) ? event.resultIndex : 0;

      for (let i = firstChanged; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = normalize(result?.[0]?.transcript || '');

        if (result?.isFinal) {
          if (text) cycle.finalByIndex.set(i, text);
          else cycle.finalByIndex.delete(i);
          cycle.interimByIndex.delete(i);
        } else {
          if (text) cycle.interimByIndex.set(i, text);
          else cycle.interimByIndex.delete(i);
        }
      }

      // Interim results may shrink or disappear between events. They are preview-only
      // and are never folded into the durable transcript.
      for (const index of [...cycle.interimByIndex.keys()]) {
        if (index >= event.results.length) cycle.interimByIndex.delete(index);
      }
    };

    recognizer.onerror = (event) => {
      if (!isCurrentCycle(cycle)) return;

      const error = event?.error || '';
      const fatal = error === 'not-allowed' || error === 'service-not-allowed' || error === 'audio-capture';
      if (!fatal) return;

      foldCycleOnce(cycle);
      cycle.ended = true;
      activeCycle = null;
      clearRestartTimer();
      clearStopFallbackTimer();

      generation += 1; // invalidate late callbacks before aborting
      controllerState = 'idle';
      try { recognizer.abort(); } catch (_) {}

      const transcript = controllerTranscript();
      if (transcript && !outputCommitted) {
        outputCommitted = true;
        input.value = combine(startingText, transcript);
        resizeInput();
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      setVoiceUi('error', 'Microphone unavailable');
      input.focus();
    };

    recognizer.onend = () => {
      if (!isCurrentCycle(cycle) || cycle.ended) return;

      cycle.ended = true;
      foldCycleOnce(cycle);
      activeCycle = null;

      if (controllerState === 'stopping') {
        finishStop(cycle.generation);
        return;
      }

      if (controllerState === 'listening' || controllerState === 'starting') {
        scheduleRecognitionRestart(cycle.generation);
      }
    };

    try {
      recognizer.start();
    } catch (_) {
      if (!isCurrentCycle(cycle)) return;
      cycle.ended = true;
      activeCycle = null;
      if (controllerState === 'stopping') finishStop(expectedGeneration);
      else scheduleRecognitionRestart(expectedGeneration);
    }
  }

  function startListening() {
    if (!SpeechRecognition || controllerState !== 'idle' || micButton.disabled) {
      if (!SpeechRecognition) setVoiceUi('error', 'Voice input unavailable');
      return;
    }

    stopPlayback();
    clearRestartTimer();
    clearStopFallbackTimer();

    generation += 1;
    startingText = normalize(input.value);
    committedCycles = [];
    outputCommitted = false;
    activeCycle = null;
    controllerState = 'starting';
    setVoiceUi('listening');
    startRecognitionCycle(generation);
    input.focus();
  }

  function stopListening() {
    if (!['starting', 'listening', 'restart-wait'].includes(controllerState)) return;

    const stopGeneration = generation;
    controllerState = 'stopping';
    clearRestartTimer();
    setVoiceUi('transcribing');

    const cycle = activeCycle;
    const recognizer = cycle?.recognition || null;

    if (!recognizer) {
      finishStop(stopGeneration);
      return;
    }

    try {
      recognizer.stop();
    } catch (_) {
      finishStop(stopGeneration);
      return;
    }

    stopFallbackTimer = window.setTimeout(() => {
      if (stopGeneration !== generation || controllerState !== 'stopping') return;
      finishStop(stopGeneration);
    }, 1800);
  }

  function cancelVoice() {
    clearRestartTimer();
    clearStopFallbackTimer();
    clearStatusTimer();

    const recognizer = activeCycle?.recognition || null;
    generation += 1; // invalidate first; any later browser callback becomes harmless
    activeCycle = null;
    controllerState = 'idle';
    committedCycles = [];
    outputCommitted = false;
    startingText = '';

    if (recognizer) {
      try { recognizer.abort(); } catch (_) {}
    }
    setVoiceUi('idle');
  }

  function setPlaybackUi(button, state) {
    if (!button) return;
    button.classList.toggle('is-loading', state === 'loading');
    button.classList.toggle('is-playing', state === 'playing');
    button.classList.toggle('is-error', state === 'error');
    button.setAttribute('aria-pressed', state === 'playing' ? 'true' : 'false');
    if (state === 'playing') {
      button.setAttribute('aria-label', 'Stop reading response');
      button.title = 'Stop reading';
    } else if (state === 'loading') {
      button.setAttribute('aria-label', 'Preparing voice playback');
      button.title = 'Preparing voice…';
    } else if (state === 'error') {
      button.setAttribute('aria-label', 'Voice playback unavailable');
      button.title = 'Voice playback unavailable — try again';
    } else {
      button.setAttribute('aria-label', 'Play response aloud');
      button.title = 'Play response';
    }
  }

  function cleanupAudioElement() {
    if (activeAudio) {
      try {
        activeAudio.pause();
        activeAudio.src = '';
      } catch (_) {}
    }
    activeAudio = null;
    if (activeObjectUrl) URL.revokeObjectURL(activeObjectUrl);
    activeObjectUrl = null;
  }

  function stopPlayback() {
    if (playbackAbortController) playbackAbortController.abort();
    playbackAbortController = null;
    cleanupAudioElement();
    if (activePlaybackButton) setPlaybackUi(activePlaybackButton, 'idle');
    activePlaybackButton = null;
  }

  function responseTextForButton(button) {
    const row = button.closest('.sowelu-guide__message--assistant');
    const content = row?.querySelector('.sowelu-guide__bubble-content') || row?.querySelector('.sowelu-guide__bubble');
    return String(content?.innerText || '')
      .replace(/https:\/\/\S+/gi, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 3900);
  }

  function speakerMarkup() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9v6h4l5 4V5l-5 4H6Z" fill="currentColor"/><path d="M18.2 8.2a5.4 5.4 0 0 1 0 7.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  }

  function ensurePlaybackButton(row) {
    if (!row || row.classList.contains('sowelu-guide__message--thinking')) return;
    const bubble = row.querySelector('.sowelu-guide__bubble');
    if (!bubble || bubble.querySelector('.sowelu-guide__playback')) return;

    let content = bubble.querySelector('.sowelu-guide__bubble-content');
    if (!content) {
      content = document.createElement('div');
      content.className = 'sowelu-guide__bubble-content';
      while (bubble.firstChild) content.appendChild(bubble.firstChild);
      bubble.appendChild(content);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sowelu-guide__playback';
    button.innerHTML = speakerMarkup();
    setPlaybackUi(button, 'idle');
    bubble.appendChild(button);
  }

  async function playResponse(button) {
    if (!button || !speechEndpoint || !/^https:\/\//i.test(speechEndpoint)) return;

    if (activePlaybackButton === button && (activeAudio || playbackAbortController)) {
      stopPlayback();
      return;
    }

    if (['starting', 'listening', 'restart-wait'].includes(controllerState)) stopListening();
    if (controllerState === 'stopping') return;
    stopPlayback();
    activePlaybackButton = button;
    setPlaybackUi(button, 'loading');

    try {
      let blob = audioCache.get(button);
      if (!blob) {
        const text = responseTextForButton(button);
        if (!text) throw new Error('No speakable response text');

        const controller = new AbortController();
        playbackAbortController = controller;
        const response = await fetch(speechEndpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
          signal: controller.signal
        });
        playbackAbortController = null;
        if (!response.ok) throw new Error(`Voice request failed: ${response.status}`);
        blob = await response.blob();
        if (!blob.size) throw new Error('Voice response was empty');
        audioCache.set(button, blob);
      }

      if (activePlaybackButton !== button) return;
      activeObjectUrl = URL.createObjectURL(blob);
      const audio = new Audio(activeObjectUrl);
      activeAudio = audio;
      audio.preload = 'auto';
      audio.onended = () => {
        if (activePlaybackButton === button) stopPlayback();
      };
      audio.onerror = () => {
        if (activePlaybackButton === button) {
          cleanupAudioElement();
          setPlaybackUi(button, 'error');
          activePlaybackButton = null;
        }
      };
      await audio.play();
      if (activePlaybackButton === button) setPlaybackUi(button, 'playing');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (activePlaybackButton === button) {
        cleanupAudioElement();
        setPlaybackUi(button, 'error');
        activePlaybackButton = null;
      }
    }
  }

  messages.querySelectorAll('.sowelu-guide__message--assistant').forEach(ensurePlaybackButton);

  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('.sowelu-guide__message--assistant')) ensurePlaybackButton(node);
        node.querySelectorAll?.('.sowelu-guide__message--assistant').forEach(ensurePlaybackButton);
      });
    });
  });
  observer.observe(messages, { childList: true, subtree: true });

  document.addEventListener('click', (event) => {
    const mic = event.target.closest('#sowelu-crystal-guide [data-guide-mic]');
    if (mic) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (['starting', 'listening', 'restart-wait'].includes(controllerState)) stopListening();
      else if (controllerState === 'idle') startListening();
      return;
    }

    const playback = event.target.closest('#sowelu-crystal-guide .sowelu-guide__playback');
    if (playback) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      playResponse(playback);
      return;
    }

    if (event.target.closest('#sowelu-crystal-guide [data-guide-close], #sowelu-crystal-guide [data-guide-reset]')) {
      cancelVoice();
      stopPlayback();
    }
  }, true);

  form.addEventListener('submit', (event) => {
    if (['starting', 'listening', 'restart-wait', 'stopping'].includes(controllerState)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (controllerState !== 'stopping') stopListening();
      return;
    }
    stopPlayback();
  }, true);

  window.addEventListener('beforeunload', () => {
    observer.disconnect();
    cancelVoice();
    stopPlayback();
  });

  setVoiceUi('idle');
})();
